import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import React, { useState } from 'react';
import { toast } from 'sonner';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { PoliceReportAttestationStatus } from '@/services/api/generated/policeReportsService';
import { usePoliceReportsUpdateAttestationStatusMutation } from '@/services/api/policeReportsService';

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  departmentName,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  departmentName: string;
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl border border-gray-200 max-w-md w-full mx-4 p-6">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <CheckCircleSolidIcon className="h-6 w-6 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Mark Report as Received
            </h3>
          </div>

          <div className="space-y-2">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm font-medium text-blue-900">
                {departmentName}
              </p>
            </div>
            <p className="text-gray-600 leading-relaxed">
              Marking this report as received will close the investigation. Has
              the police report been added to the matter?
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Updating...' : 'Yes, Mark as Received'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const StatusControls = ({
  reportId,
  matterId,
  currentStatus,
  departmentName,
  onUpdate,
}: {
  reportId: string;
  matterId: string;
  currentStatus: PoliceReportAttestationStatus | string;
  departmentName: string;
  onUpdate?: () => void;
}) => {
  const [updateStatus, { isLoading }] =
    usePoliceReportsUpdateAttestationStatusMutation();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleStatusClick = async (
    e: React.MouseEvent,
    status: PoliceReportAttestationStatus,
  ) => {
    e.stopPropagation();

    // Show confirmation modal for verified received status
    if (status === 'verified_received') {
      setShowConfirmModal(true);
      return;
    }

    // For other statuses, update directly
    await updateStatusDirectly(status);
  };

  const updateStatusDirectly = async (
    status: PoliceReportAttestationStatus,
  ) => {
    try {
      // Convert enum to string literal value
      const statusValue = status.valueOf() as
        | 'verified_received'
        | 'attested_verified'
        | 'rejected_verified'
        | 'attested_unverified'
        | 'inferred'
        | 'unclear';

      await updateStatus({
        id: reportId,
        matterId,
        updatePoliceReportAttestationStatus: {
          attestation_status: statusValue,
        },
      }).unwrap();

      toast.success('Report status updated successfully');
      onUpdate?.();
    } catch (error) {
      const errorMessage =
        error &&
        typeof error === 'object' &&
        'data' in error &&
        typeof (error as { data: unknown }).data === 'object'
          ? JSON.stringify((error as { data: object }).data)
          : 'Unknown error';
      toast.error(`Failed to update status: ${errorMessage}`);
    }
  };

  const handleConfirmReceived = async () => {
    await updateStatusDirectly('verified_received');
    setShowConfirmModal(false);
  };

  return (
    <>
      <div className="flex items-center gap-1">
        {/* No Report (X) */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              disabled={isLoading}
              onClick={(e) => handleStatusClick(e, 'rejected_verified')}
              className={`p-1.5 rounded transition-colors disabled:opacity-50 ${
                currentStatus === 'rejected_verified'
                  ? 'bg-red-100 text-red-600'
                  : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
              }`}
              aria-label="Mark as no report"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            Department confirmed they don&apos;t have a report
          </TooltipContent>
        </Tooltip>

        {/* Confirmed (Check) */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              disabled={isLoading}
              onClick={(e) => handleStatusClick(e, 'attested_verified')}
              className={`p-1.5 rounded transition-colors disabled:opacity-50 ${
                currentStatus === 'attested_verified' ||
                currentStatus === 'attested_unverified'
                  ? 'bg-blue-100 text-blue-600'
                  : 'text-gray-400 hover:text-blue-500 hover:bg-blue-50'
              }`}
              aria-label="Mark as confirmed"
            >
              <CheckIcon className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            Department confirmed they have the report
          </TooltipContent>
        </Tooltip>

        {/* Verified Received (Double Check) */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              disabled={isLoading}
              onClick={(e) => handleStatusClick(e, 'verified_received')}
              className={`p-1.5 rounded transition-colors disabled:opacity-50 ${
                currentStatus === 'verified_received'
                  ? 'bg-green-100 text-green-600'
                  : 'text-gray-400 hover:text-green-500 hover:bg-green-50'
              }`}
              aria-label="Mark as received"
            >
              <CheckCircleSolidIcon className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            We have obtained the report from this department
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmReceived}
        isLoading={isLoading}
        departmentName={departmentName}
      />
    </>
  );
};
